import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { IFooter, ILanguage } from "../../../models/Interfaces";
import { HeaderFooterService } from "../../../services/HeaderFooterService";
import { Button, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import Loading from "../../components/common/Loading";
import { Column, CellProps } from 'react-table';
import AddUpdateFooterModal from "./AddUpdateFooter";
import { ContentService } from "../../../services/ContentService";
import { useConfirm } from "../../../config/ConfirmProvider";
import FilteredTable from "../../components/common/FilteredTable";
import styles from "./footers.module.scss";

interface ISiteFooters {

}

const SiteHeaders = (props: ISiteFooters) => {
  const [footers, setFooters] = useState<Array<IFooter>>([]);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [selectedFooter, setSelectedFooter] = useState<IFooter | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);

  const footerSvcRef = useRef<HeaderFooterService>();
  const contentSvcRef = useRef<ContentService>();
  const confirmation = useConfirm();

  const columns: Column<IFooter>[] = useMemo(() => [
    { Header: "Title", accessor: "Title" },
    {
      id: "Language",
      Header: "Language",
      Cell: ({ row }: CellProps<IFooter>) => (
        <>{getLanguage(row.original.Language ?? "")}</>
      )
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IFooter>) => (
        <div className="user-result-actions user-result-actions--single">
          <Button variant="light" onClick={() => editFooter(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="edit footer">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteFooter(row.original.Id)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete footer">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [languages, footers]);

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const langs = await contentSvcRef.current.getSiteLanguages();
      if (langs) {
        setLanguages(langs);
      }
    }
  };

  const getFooters = async (): Promise<void> => {
    if (footerSvcRef.current) {
      const footers = await footerSvcRef.current.getSiteFooters(false);

      if (footers.length > 0) {
        setFooters(footers);
      }
    }
  };

  const getLanguage = (shortcode: string) => {
    return languages?.find(l => l.ShortCode === shortcode)?.Language ?? shortcode;
  };

  const addFooter = (): void => {
    if (footerSvcRef.current) {
      const newfooter = footerSvcRef.current.newSiteFooter();
      if (newfooter) {
        setSelectedFooter(newfooter);
        setShowModal(true);
      }
    }
  };

  const editFooter = (footer: IFooter): void => {
    setSelectedFooter(footer);
    setShowModal(true);
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedFooter, (item) => item.Id, () => closeDetails());

  const deleteFooter = async (footerid: number): Promise<void> => {
    if (footerSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation("Delete this footer? <b>This action cannot be undone.</b>", "Delete Footer");

      if (confirmed) {
        const delres = await footerSvcRef.current.deleteSiteFooter(footerid);

        if (delres) {
          setFooters((prev) => {
            const filtered = prev.filter(f => f.Id !== footerid);
            return filtered;
          });
          closeDeletedSelection(footerid);
        }
      }
    }
  };

  const saveFooter = async (footer: IFooter): Promise<void> => {
    if (footerSvcRef.current) {
      const saved = await footerSvcRef.current.saveSiteFooter(footer);

      if (saved) {
        const existing = footers.find(h => h.Id === saved.Id);

        if (existing) {
          const updated = footers.map(h => h.Id === saved.Id ? saved : h);
          setFooters(updated);
        } else {
          setFooters(prev => ([
            ...prev,
            saved
          ]));
        }
        closeDetails();
      }
    }
  };

  const closeDetails = (): void => {
    setSelectedFooter(null);
    setShowModal(false);
  }

  useEffect(() => {
    let active = true;
    contentSvcRef.current ??= new ContentService();
    footerSvcRef.current ??= new HeaderFooterService();
    setLoading(true);
    void Promise.allSettled([getLanguages(), getFooters()]).then(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  return (
    <>
      {loading && (
        <Row>
          <Col md={12} className="text-center mt-5">
            <Loading Message="loading available site footers..." SpinSize="5x" key={"LoadingSpinner"}></Loading>
          </Col>
        </Row>
      )}

      {!loading && languages?.length > 0 && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div>
                <h2 className="app-list-section__title">Footer Library</h2>
              </div>
            </div>
            <div className="app-list-section__body">
            <FilteredTable<IFooter>
              columns={columns}
              data={footers}
              noDataMessage="No site footers found, click the add button above to add a new footer"
              showAddButton={true}
              addButtonText="Add a site footer"
              addButtonIcon={faPlus}
              addButtonHandler={addFooter}
              filterPlaceHolder="filter footers..."
            />
            </div>
          </section>
        </div>
      )}

      {!loading && selectedFooter && showModal && (
        <AddUpdateFooterModal
          footer={selectedFooter}
          onClose={closeDetails}
          onSave={(ftr) => saveFooter(ftr)}
          key={"addUpdateFooterModal"}
          languages={languages ?? []}
        />
      )}
    </>
  );
};

export default SiteHeaders;
